/**
 * Auth native API contracts.
 *
 * Defines the request/response schemas for native PKCE authentication.
 * Used by both server (API) and clients (desktop/mobile).
 */

import { oc } from "@orpc/contract";
import { z } from "zod";

// ─────────────────────────────────────────────────────────────────────────────
// Shared Paths
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Web app path for native login flow.
 * Desktop/mobile apps open this in browser for PKCE OAuth.
 */
export const NATIVE_LOGIN_PATH = "/auth/native-login";

export const ClientTypeSchema = z.enum(["web", "desktop", "mobile", "api"]);
export type ClientType = z.infer<typeof ClientTypeSchema>;

export const AuthUserSchema = z.object({
  id: z.string(),
  email: z.string().nullable(),
  name: z.string().nullable(),
  role: z.string(),
  isAnonymous: z.boolean(),
  image: z.string().nullable(),
  emailVerified: z.boolean(),
});
export type AuthUser = z.infer<typeof AuthUserSchema>;

export const NativeSessionSchema = z.object({
  token: z.string(),
  expiresAt: z.number(),
  user: AuthUserSchema,
});
export type NativeSession = z.infer<typeof NativeSessionSchema>;

export const AuthErrorSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
  }),
});
export type AuthError = z.infer<typeof AuthErrorSchema>;

export const AuthorizeRequestSchema = z.object({
  code_challenge: z.string().min(43).max(128),
  code_challenge_method: z.literal("S256"),
  redirect_uri: z.string().url(),
  client_type: ClientTypeSchema,
});
export type AuthorizeRequest = z.infer<typeof AuthorizeRequestSchema>;

export const AuthorizeResponseSchema = z.object({
  code: z.string(),
  expiresIn: z.number(),
});
export type AuthorizeResponse = z.infer<typeof AuthorizeResponseSchema>;

/**
 * Auth native contract - PKCE OAuth endpoints for native apps.
 */
export const authNativeContract = {
  /**
   * Create authorization code for PKCE flow.
   * Called by web app after user authenticates via cookies.
   */
  authorize: oc
    .route({ method: "POST", path: "/auth/native/authorize" })
    .input(AuthorizeRequestSchema)
    .output(AuthorizeResponseSchema),

  /**
   * Exchange authorization code for session token.
   * Called by native app with code + PKCE verifier.
   */
  token: oc
    .route({ method: "POST", path: "/auth/native/token" })
    .input(
      z.object({
        code: z.string().min(32).max(64),
        code_verifier: z.string().min(43).max(128),
        redirect_uri: z.string().url(),
      })
    )
    .output(NativeSessionSchema),

  /**
   * Refresh session token.
   * Called by native app with bearer token.
   */
  refresh: oc
    .route({ method: "POST", path: "/auth/native/refresh" })
    .output(NativeSessionSchema),

  /**
   * Create anonymous session with bearer token.
   * Called by native app when no stored auth exists.
   */
  anonymous: oc
    .route({ method: "POST", path: "/auth/native/anonymous" })
    .output(NativeSessionSchema),
};
